/**
 * Request shapes for connecting AWS, shared by the three transports that
 * reach them: the signed-in staff API, the customer portal, and the public
 * /newcustomer onboarding (token-gated, no bearer). Each supplies its own
 * JSON-POST function; the endpoint suffixes and bodies are defined once here.
 */
import { request } from './core'

export interface AwsSsoDeviceAuth {
  region: string
  client_id: string
  client_secret: string
  device_code: string
  user_code: string
  verification_uri_complete: string
  verification_uri: string
  interval: number
  expires_in: number
}

export interface AwsSsoTokenResult {
  status: string
  access_token: string | null
  expires_in: number | null
  reason: string | null
}

export interface AwsSsoAccount {
  account_id: string
  account_name: string
  email: string | null
  roles: string[]
}

export interface AwsSsoRoleCredentials {
  access_key_id: string
  secret_access_key: string
  session_token: string
  expiration: number | null
}

export interface AwsSsoResolvedAccount {
  account: AwsSsoAccount
  role_name: string
  credentials: AwsSsoRoleCredentials
  expires_at?: string | null
}

export interface AwsSsoProvisionedAccessKeys {
  access_key_id: string
  secret_access_key: string
  expiration: string
  user_name: string
}

/** The IAM Identity Center device-flow endpoints the SSO wizard drives. */
export interface AwsSsoConnectionApi {
  start: (startUrl: string, region: string) => Promise<AwsSsoDeviceAuth>
  token: (p: { clientId: string; clientSecret: string; deviceCode: string; region: string }) => Promise<AwsSsoTokenResult>
  accounts: (accessToken: string, region: string) => Promise<{ accounts: AwsSsoAccount[] }>
  credentials: (accessToken: string, region: string, accountId: string, roleName: string) => Promise<AwsSsoRoleCredentials>
  provisionAccessKeys: (p: {
    tempAccessKey: string
    tempSecretKey: string
    tempSessionToken: string
    accountId: string
    expirationDays: number
  }) => Promise<AwsSsoProvisionedAccessKeys>
}

/** A JSON POST over some transport. */
export type JsonPost = <T>(path: string, body: unknown) => Promise<T>

/** The device-flow client for endpoints mounted under `prefix`. */
export function ssoApiVia(prefix: string, post: JsonPost): AwsSsoConnectionApi {
  return {
    start: (startUrl, region) => post(`${prefix}/start`, { start_url: startUrl, region }),
    token: (p) =>
      post(`${prefix}/token`, {
        client_id: p.clientId,
        client_secret: p.clientSecret,
        device_code: p.deviceCode,
        region: p.region,
      }),
    accounts: (accessToken, region) => post(`${prefix}/accounts`, { access_token: accessToken, region }),
    credentials: (accessToken, region, accountId, roleName) =>
      post(`${prefix}/credentials`, {
        access_token: accessToken,
        region,
        account_id: accountId,
        role_name: roleName,
      }),
    provisionAccessKeys: (p) =>
      post(`${prefix}/provision-access-keys`, {
        temp_access_key: p.tempAccessKey,
        temp_secret_key: p.tempSecretKey,
        temp_session_token: p.tempSessionToken,
        account_id: p.accountId,
        expiration_days: p.expirationDays,
      }),
  }
}

/** POST through the signed-in API client. */
export const authedPost: JsonPost = (path, body) =>
  request(path, { method: 'POST', body: JSON.stringify(body) })

/** The staff (signed-in) device-flow endpoints. */
export const staffSsoApi = ssoApiVia('/api/aws-sso', authedPost)

export interface CredentialTestResult {
  ok: boolean
  account_id: string | null
  arn: string | null
  reason: string | null
  user_id?: string | null
  expired?: boolean
  /** Which pair was tested: keys from the form, or the saved ones. */
  source?: 'entered' | 'stored'
}

/** The connection-test body for a key pair. Keys blank → the server tests
 *  the SAVED credentials (`awsAccountId` picks the account row). */
export function credentialTestBody(
  accessKey: string,
  secretKey: string,
  expectedAccountId?: string,
  sessionToken?: string,
  awsAccountId?: string,
) {
  return {
    access_key: accessKey,
    secret_key: secretKey,
    expected_account_id: expectedAccountId,
    session_token: sessionToken,
    aws_account_id: awsAccountId,
  }
}
